/**
 * Tareas API module.
 *
 * Covers the full task lifecycle: fetch, upsert, delete, image management,
 * space settings, categories and property color.
 *
 * All functions delegate to `apiFetch`, which handles auth headers,
 * JSON parsing and Ant Design error/success toasts automatically.
 */

import { apiFetch } from "./apiClient";

// ─── tareasApi ────────────────────────────────────────────────────────────────

// ─── Document types ──────────────────────────────────────────────────────────

export interface DocumentMeta {
  provider: string;
  key: string;
  url: string;
  originalName: string;
  mimeType: string;
  size: number;
  /** Comentario opcional del documento (editable por admin, visible al cliente). */
  comentario?: string | null;
}

export const tareasApi = {
  // ── Core tasks ────────────────────────────────────────────────────────────

  /** GET /tareas/db — full task database snapshot. */
  getDb: (): Promise<any | null> =>
    apiFetch("/tareas/db", {
      errorMsg: "Error al obtener la base de tareas",
    }),

  /** GET /tareas — list all tasks. */
  list: (): Promise<any | null> =>
    apiFetch("/tareas", {
      errorMsg: "Error al obtener tareas",
    }),

  /** POST /tareas — create or update a task (upsert by ID). */
  upsert: (payload: Record<string, any>): Promise<any | null> =>
    apiFetch("/tareas", {
      method: "POST",
      body: JSON.stringify(payload),
      errorMsg: "Error al guardar la tarea",
    }),

  /** DELETE /tareas/:id */
  delete: (id: string): Promise<any | null> =>
    apiFetch(`/tareas/${id}`, {
      method: "DELETE",
      errorMsg: "Error al eliminar la tarea",
    }),

  // ── Images ────────────────────────────────────────────────────────────────

  /** GET /tareas/:id/images */
  getImages: (id: string): Promise<any | null> =>
    apiFetch(`/tareas/${id}/images`, {
      errorMsg: "Error al obtener imágenes",
    }),

  /** POST /tareas/:id/images — upload via FormData (multipart). */
  uploadImage: (id: string, formData: FormData): Promise<any | null> =>
    apiFetch(`/tareas/${id}/images`, {
      method: "POST",
      body: formData,
      errorMsg: "Error al subir la imagen",
    }),

  /** DELETE /tareas/:id/images — delete an image by filename. */
  deleteImage: (id: string, filename: string): Promise<any | null> =>
    apiFetch(`/tareas/${id}/images`, {
      method: "DELETE",
      body: JSON.stringify({ filename }),
      errorMsg: "Error al eliminar la imagen",
    }),

  // ── Documents ─────────────────────────────────────────────────────────────

  /** GET /tareas/:id/documents */
  getDocuments: (id: string): Promise<any | null> =>
    apiFetch(`/tareas/${id}/documents`, {
      errorMsg: "Error al obtener documentos",
    }),

  /** POST /tareas/:id/documents — upload via FormData (multipart). */
  uploadDocument: (id: string, formData: FormData): Promise<any | null> =>
    apiFetch(`/tareas/${id}/documents`, {
      method: "POST",
      body: formData,
      errorMsg: "Error al subir el documento",
    }),

  /** DELETE /tareas/:id/documents?key=... */
  deleteDocument: (id: string, key: string): Promise<any | null> =>
    apiFetch(`/tareas/${id}/documents?key=${encodeURIComponent(key)}`, {
      method: "DELETE",
      errorMsg: "Error al eliminar el documento",
    }),

  // ── Space settings & categories ───────────────────────────────────────────

  /** PATCH /tareas/spaces/:spaceKey/settings */
  patchSpaceSettings: (
    spaceKey: string,
    settings: Record<string, any>,
  ): Promise<any | null> =>
    apiFetch(`/tareas/spaces/${spaceKey}/settings`, {
      method: "PATCH",
      body: JSON.stringify(settings),
      errorMsg: "Error al actualizar la configuración del espacio",
    }),

  /** POST /tareas/spaces/:spaceKey/categories */
  createCategory: (
    spaceKey: string,
    payload: Record<string, any>,
  ): Promise<any | null> =>
    apiFetch(`/tareas/spaces/${spaceKey}/categories`, {
      method: "POST",
      body: JSON.stringify(payload),
      errorMsg: "Error al crear la categoría",
    }),

  /** PATCH /tareas/spaces/:spaceKey/categories/:categoryId */
  patchCategory: (
    spaceKey: string,
    categoryId: string,
    payload: Record<string, any>,
  ): Promise<any | null> =>
    apiFetch(`/tareas/spaces/${spaceKey}/categories/${categoryId}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
      errorMsg: "Error al actualizar la categoría",
    }),

  /** DELETE /tareas/spaces/:spaceKey/categories/:categoryId */
  deleteCategory: (spaceKey: string, categoryId: string): Promise<any | null> =>
    apiFetch(`/tareas/spaces/${spaceKey}/categories/${categoryId}`, {
      method: "DELETE",
      errorMsg: "Error al eliminar la categoría",
    }),

  // ── Property color ────────────────────────────────────────────────────────

  /** PATCH /tareas/properties/:id/color */
  patchPropertyColor: (id: number, color: string): Promise<any | null> =>
    apiFetch(`/tareas/properties/${id}/color`, {
      method: "PATCH",
      body: JSON.stringify({ color }),
      errorMsg: "Error al actualizar el color",
    }),

  // ── Comentarios por attachment (foto/video/doc) — admin/superadmin only ──

  /**
   * PATCH /tareas/:id/attachment-comment
   * Body: { kind: "imagenes"|"documentos", key: string, comentario: string|null }
   * Devuelve la lista actualizada con URLs firmadas. Pasar `comentario=""` o
   * null elimina el comentario. El cliente puede leer el comentario del
   * GET de la tarea — no necesita permisos extra.
   */
  setAttachmentComment: (
    id: string,
    body: { kind: "imagenes" | "documentos"; key: string; comentario: string | null },
  ): Promise<any | null> =>
    apiFetch(`/tareas/${id}/attachment-comment`, {
      method: "PATCH",
      body: JSON.stringify(body),
      successMsg: body.comentario ? "Comentario guardado" : "Comentario eliminado",
      errorMsg: "Error al guardar el comentario",
    }),

  /**
   * POST /tareas/:id/images/mute?key=...
   * Quita la pista de audio de un video (admin/superadmin only).
   * Operación destructiva: el audio original no se puede recuperar.
   */
  muteVideo: (id: string, key: string): Promise<any | null> =>
    apiFetch(`/tareas/${id}/images/mute?key=${encodeURIComponent(key)}`, {
      method: "POST",
      successMsg: "Audio quitado correctamente",
      errorMsg: "Error al quitar el audio",
    }),
};
